// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { ReactNode, useState } from 'react';
import { Box, useTheme } from '@mui/system';
import { Button } from '@mui/material';
import stylesDef from './styles';

export const HISTORY_FOLD_LIMIT = 5;

// Defaults live in the destructuring: defaultProps is deprecated for function components.
/* eslint-disable react/require-default-props */
interface IHistoryListProps<T> {
  // Newest first, as the server serves a change log.
  entries: T[];
  // One row of the log; the caller owns the columns.
  renderRow: (entry: T) => ReactNode;
  getKey: (entry: T) => string;
  // How many rows show before the fold.
  limit?: number;
}
/* eslint-enable react/require-default-props */

// A change log folded to its newest rows, with one button to show the rest and fold it again. The
// Subscription tab's four logs (credit adjustments, PAYG rate, overage rate, license fee) share it
// so a long-lived account's tab stays readable and the fold reads the same on each.
export function HistoryList<T>({ entries, renderRow, getKey, limit = HISTORY_FOLD_LIMIT }: IHistoryListProps<T>) {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const [expanded, setExpanded] = useState(false);

  if (entries.length === 0) return null;

  const folded = entries.length > limit && !expanded;
  const shown = folded ? entries.slice(0, limit) : entries;

  return (
    <Box sx={styles.list}>
      {shown.map((entry) => (
        <React.Fragment key={getKey(entry)}>{renderRow(entry)}</React.Fragment>
      ))}
      {entries.length > limit && (
        <Button variant="text" size="small" sx={styles.foldButton} onClick={() => setExpanded(!expanded)}>
          {folded ? `Show all (${entries.length})` : 'Show less'}
        </Button>
      )}
    </Box>
  );
}

export default HistoryList;
